<template>
  <v-app-bar
    class="app-bar justify-center ssr-fix-header"
    :height="height"
    :class="isOnHomepage ? 'ui-kit-bg-gray-0' : 'border-bottom'"
    flat
  >
    <v-container class="d-flex align-center px-3">
      <slot name="prepend-logo" />
      <NuxtLink
        aria-label="Перейти на главную"
        :to="withRouteParams({ name: 'index' })"
      >
        <v-img
          height="32"
          alt="МедТочка"
          :src="locale.logo"
          :width="locale.isCis ? 215 : 150"
          @click="handleLogoClick"
        />
      </NuxtLink>

      <v-spacer />

      <div
        v-if="isTargetGroupButtonsVisible"
        class="mr-8 gc-8 d-none d-md-flex"
      >
        <NuxtLink
          v-for="button in headerButtons"
          :key="button.text"
          class="app-bar__header-button cursor-pointer
          text-landing-body-2 font-weight-semi-bold text-decoration-none"
          :class="{ 'text-primary': $route.name === button.type }"
          :to="button.to"
          replace
        >
          {{ button.text }}
        </NuxtLink>
      </div>

      <v-spacer />
      <v-btn
        v-if="!hideMobileCountrySelect"
        class="d-md-none"
        color="ui-kit-text-info"
        title="меню"
        :icon="buttonMenuIcon"
        @click="toggleDrawer"
      />
      <HeaderCountrySelect class="d-none d-md-flex mr-8" />
      <div class="flex-column d-none d-md-flex">
        <BaseMtButton
          variant="tonal"
          class="ml-auto d-none d-sm-block app-bar__login-button"
          @click="handleClickLogin"
        >
          Войти
        </BaseMtButton>
        <BaseMtButton
          variant="tonal"
          class="ml-auto d-sm-none app-bar__login-button"
          small
          @click="handleClickLogin"
        >
          Войти
        </BaseMtButton>

        <v-menu
          v-model="isMenuActive"
          location="bottom"
          :close-on-content-click="false"
        >
          <v-card
            width="273"
            class="pa-4"
          >
            <div>
              <p class="text-subtitle-1 mb-2">
                МедТочка для пациента
              </p>
              <p class="mb-4 text-caption text-ui-kit-text-secondary">
                Управляйте записями и ведите медкарту
              </p>
              <BaseMtButton
                target="_blank"
                :href="authorizationUrl"
                small
                @click="isMenuActive = false"
              >
                Войти
              </BaseMtButton>
            </div>

            <v-divider class="mt-4" />

            <div class="mt-4">
              <p class="text-subtitle-1">
                МедТочка для врача
              </p>
              <p class="mt-2 mb-0 text-caption text-ui-kit-text-secondary">
                Просматривайте медкарты пациентов и отправляйте документы
              </p>
              <BaseMtButton
                class="mt-4"
                target="_blank"
                :href="locale.mtVrachUrl"
                small
                @click="isMenuActive = false"
              >
                Войти
              </BaseMtButton>
            </div>
          </v-card>
          <template #activator="{ props }">
            <span v-bind="props" />
          </template>
        </v-menu>
      </div>
    </v-container>
  </v-app-bar>
  <client-only>
    <v-navigation-drawer
      v-model="isShowDrawer"
      order="1"
      :width="displayWidth"
      disable-resize-watcher
      disable-route-watcher
      floating
      touchless
      persistent
    >
      <nuxt-link
        v-for="(link) in drawerLinks"
        :key="link.text"
        class="text-decoration-none text-ui-kit-text"
        :to="link.to"
        external
      >
        <v-list-item
          v-ripple
          class="pa-4"
          :title="link.text"
        >
          <template #prepend>
            <v-icon
              color="ui-kit-icon-secondary"
              :icon="link.icon"
            />
          </template>
        </v-list-item>
      </nuxt-link>
      <v-divider class="ma-4" />
      <CountrySelectBottomSheet />
      <v-divider class="ma-4" />
      <v-list-item
        class="pa-4"
        :active="false"
        :to="withRouteParams({ name: 'about-the-project' })"
        @click="isShowDrawer = false"
      >
        О МедТочке
        <template #append>
          <v-icon
            icon="ui-icon-arrow-right"
            color="ui-kit-icon-secondary"
          />
        </template>
      </v-list-item>
      <v-list-item
        class="pa-4 pa-md-3"
        :active="false"
        :to="withRouteParams({ name: 'contacts' })"
        @click="isShowDrawer = false"
      >
        Контакты
        <template #append>
          <v-icon
            icon="ui-icon-arrow-right"
            color="ui-kit-icon-secondary"
          />
        </template>
      </v-list-item>
      <TermsOfUseBottomsheet @navigate="isShowDrawer = false" />
      <v-tooltip
        z-index="0"
        scroll-strategy="block"
        content-class="d-none"
        title="меню тултип"
        :model-value="isShowDrawer"
      />
    </v-navigation-drawer>
  </client-only>
</template>

<script setup lang="ts">
import { useGoTo, useDisplay } from 'vuetify'
import { FEATURE_ROUTE_NAME } from '~/enums/features'

type Props = {
  hideMobileCountrySelect?: boolean
}

defineProps<Props>()
const locale = useAppLocale()
const { height } = useHeaderHeight()
const goTo = useGoTo()

const isShowDrawer = ref(false)
const { width: displayWidth, mdAndUp } = useDisplay()

watch(mdAndUp, (value) => {
  if (value) {
    isShowDrawer.value = false
  }
})

const buttonMenuIcon = computed(() => {
  return isShowDrawer.value
    ? 'ui-icon-close-not-a-circle'
    : 'ui-icon-menu'
})

const drawerLinks = computed(() => [
  {
    to: authorizationUrl.value,
    text: 'Войти как пациент',
    icon: 'ui-icon-user-only',
  },
  {
    to: locale.mtVrachUrl,
    text: 'Войти как врач',
    icon: 'ui-icon-doctor',
  },
])

function toggleDrawer() {
  isShowDrawer.value = !isShowDrawer.value
}

const headerButtons = [
  {
    to: withRouteParams({
      name: FEATURE_ROUTE_NAME.PATIENT,
      hash: '#feature-section',
    }),
    text: 'Пациентам',
    type: FEATURE_ROUTE_NAME.PATIENT,
  },
  {
    to: withRouteParams({
      name: FEATURE_ROUTE_NAME.DOCTOR,
      hash: '#feature-section',
    }),
    text: 'Врачам',
    type: FEATURE_ROUTE_NAME.DOCTOR,
  },
  ...(locale.isRu
    ? [{
      to: withRouteParams({
        name: FEATURE_ROUTE_NAME.CLINIC,
        hash: '#feature-section',
      }),
      text: 'Клиникам',
      type: FEATURE_ROUTE_NAME.CLINIC,
    }]
    : []
  ),
]

const isMenuActive = ref(false)

const $route = useRoute()

const isOnHomepage = computed(() => $route.name?.toString().startsWith('index') ?? false)
const isTargetGroupButtonsVisible = computed(() => Object.values(FEATURE_ROUTE_NAME).includes($route.name as string))

const authorizationUrl = computed(
  () => `${useRuntimeConfig().public.mtUrl}/authorization/?preselected_country=${locale.currentCountry}`,
)

function handleLogoClick() {
  if ($route.name === 'index') {
    goTo(0)
  }
}

function handleClickLogin() {
  isMenuActive.value = true
}
</script>

<style scoped lang="scss">
@use '@/assets/vuetify/settings';
@use 'sass:map';

.app-bar {
  &__header-button {
    color: $t-black;

    &:hover {
      color: $t-primary
    }
  }

  &__login-button {
    font-size: 14px;

    @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
      font-size: 18px;
    }
  }
}

.border-bottom {
  border-bottom: 1px solid $t-ui-bg-gray-40;
}
</style>
